import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks/redux';
import { cloneDeep, find, isEqual, sortBy } from 'lodash';
import { setElementValues } from 'store/rfiSlice';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { answerValueTypes, ElementAnswer, ElementOption, elementTypes, ElementValue, IElementProps, optionTypes } from '../../types/RfiTypes';
import { OTHER_DEFAULT_LABEL } from './ElementEditor';

const CheckboxGroup = (props: IElementProps) => {
  // Hooks
  const dispatch = useAppDispatch();

  // State
  const [hasOptionsChanged, setOptionsChanged] = useState<boolean>(false);
  const [hasOtherChanged, setOtherChanged] = useState<boolean>(false);
  const [checkedValues, setCheckedValues] = useState<ElementValue[]>(() => {
    return props.element.answers
      ? cloneDeep(props.element.answers)
          .filter(
            (answer: ElementAnswer) =>
              answer?.answerValueType === answerValueTypes.OPTION &&
              props.element.options?.find(
                (option: ElementOption) =>
                  option?.type === optionTypes.DEFAULT &&
                  option?.value &&
                  option.value === answer.value
              )
          )
          ?.map((a: ElementAnswer) => {
            return {
              value: a?.value ?? "",
              answerValueType: answerValueTypes.OPTION,
            };
          }) ?? []
      : [];
  });
  const [otherValue, setOtherValue] = useState<string>(
    () =>
      props.element.answers?.find(
        (a: ElementAnswer) => a.answerValueType === answerValueTypes.OTHER
      )?.value ?? ""
  );

  const options = sortBy(props.element.options, (option: ElementOption) => {
    return option.sequence;
  });
  let otherOption = find(props.element.options, function (o: ElementOption) {
    return o.type === optionTypes.OTHER;
  });

  // Effects
  useEffect(() => {
    let toSaveChecked =
      checkedValues.map((checked: ElementAnswer) => {
        return {
          value: checked.value,
          answerValueType: answerValueTypes.OPTION,
        };
      }) ?? [];

    if (otherValue) {
      toSaveChecked.push({
        value: otherValue ?? "",
        answerValueType: answerValueTypes.OTHER,
      });
    }

    if (hasOptionsChanged || hasOtherChanged) {
      //props.element.setValues(toSaveChecked);

      dispatch(
        setElementValues({
          elementId: props.element.elementId,
          values: toSaveChecked,
        })
      );

      setOptionsChanged(false);
      setOtherChanged(false);
    }
  }, [checkedValues, otherValue, props.element]);

  // Handlers
  const onCheckboxChange = (event: CheckboxChangeEvent) => {
    if (event.target.value) {
      // See if option already Checked
      if (
        event.target.value &&
        checkedValues.find(
          (answer: ElementAnswer) => answer.value === event.target.name
        )
      ) {
        return;
      }
      // Check Option
      if (event.target.value) {
        setOptionsChanged(true);
        setCheckedValues([
          ...checkedValues,
          {
            value: event.target.name ?? "",
            answerValueType: answerValueTypes.OPTION,
          },
        ]);
      }
    }
    // Uncheck option
    else {
      setOptionsChanged(true);
      setCheckedValues(
        checkedValues.filter((v: ElementValue) => v.value !== event.target.name)
      );
    }
  };

  const handleChangeOther = (event: React.FormEvent<HTMLInputElement>) => {
    setOtherChanged(true);
    setOtherValue(event.currentTarget.value ?? "");
  };

  // Render
  if (props.readOnly) {
    return (
      <div>
        <div className="col-form-label">
          {props.element.elementType === elementTypes.SURVEY
            ? `${props.questionNumber}. `
            : ""}{" "}
          {props.element.fieldLabel}{" "}
          {props.overrideRequired === undefined ? (
            props.element.requiredResponse ? (
              <span>*</span>
            ) : (
              ""
            )
          ) : props.overrideRequired === true ? (
            <span>*</span>
          ) : (
            ""
          )}
        </div>
        Options:
        <br />
        <div style={{ marginLeft: "20px" }}>
          {options
            ? options.map((option: ElementOption, index: number) => {
                return (
                  option.type === optionTypes.DEFAULT && (
                    <div key={index}>
                      {checkedValues &&
                      checkedValues.find(
                        (a: ElementAnswer) => a.value === option.value
                      ) ? (
                        <span className="k-icon k-i-checkbox-checked"></span>
                      ) : (
                        <span className="k-icon k-i-checkbox"></span>
                      )}{" "}
                      {`${
                        option?.description ??
                        `Option ${option?.sequence ?? ""}`
                      }`}{" "}
                      {checkedValues &&
                      checkedValues.find(
                        (a: ElementAnswer) => a.value === option.value
                      )
                        ? " (Selected)"
                        : ""}
                      <br />
                    </div>
                  )
                );
              })
            : ""}
          {otherOption && (
            <div style={{ marginLeft: "20px", marginRight: "10px" }}>
              <label
                htmlFor={`other-value-${props.element.elementId}`}
                className="col-form-label"
              >
                {otherOption?.description ?? OTHER_DEFAULT_LABEL}
              </label>
              <input
                id={`other-value-${props.element.elementId}`}
                className="form-control"
                value={otherValue}
                disabled
              />
              <br />
            </div>
          )}
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <fieldset className="usa-fieldset-inputs usa-sans">
          <legend className="visuallyHidden">{props.element.fieldLabel}</legend>

          <div className="col-form-label">
            {props.element.elementType === elementTypes.SURVEY
              ? `${props.questionNumber}. `
              : ""}
            {props.element.fieldLabel}{" "}
          </div>

          {options
            ? options.map((option: ElementOption, index: number) => {
                return (
                  option.type === optionTypes.DEFAULT && (
                    <div style={{ marginLeft: "20px" }} key={index}>
                      <Checkbox
                        defaultChecked={
                          props.element.answers &&
                          props.element.answers.find(
                            (e: ElementAnswer) => e.value === option.value
                          )
                            ? true
                            : false
                        }
                        value={option.value}
                        label={
                          option?.description ??
                          `Option ${option?.sequence ?? ""}`
                        }
                        key={option.value}
                        onChange={onCheckboxChange}
                        id={`cb-${option.value}`}
                        name={`${option.value}`}
                      />
                      <br />
                    </div>
                  )
                );
              })
            : ""}

          {otherOption && (
            <div style={{ marginLeft: "20px", marginRight: "10px" }}>
              <label
                className="col-form-label"
                htmlFor={`other-value-${props.element.elementId}`}
              >
                {otherOption.description ?? OTHER_DEFAULT_LABEL}
              </label>
              <input
                id={`other-value-${props.element.elementId}`}
                className="form-control"
                value={otherValue}
                disabled={props.readOnly}
                onChange={handleChangeOther}
              />
            </div>
          )}
        </fieldset>
      </div>
    );
  }
};

const areEqual = (prevProps: IElementProps, nextProps: IElementProps) => {
  return (
    !prevProps.rerender &&
    isEqual(prevProps.element, nextProps.element) &&
    isEqual(prevProps.designMode, nextProps.designMode) &&
    isEqual(prevProps.readOnly, nextProps.readOnly)
  );
};

const CheckboxElement = React.memo(CheckboxGroup, areEqual);

export default CheckboxElement;
